/**
 * SearchCardV6 - New search card model for member search and My Type filter.
 * Replaces the legacy SearchCard model.
 *
 * Translation mapping for string array fields (keys map to @i18n/locales/domain ProfileTags):
 * | SearchCardV6 prop  | Translation section |
 * |--------------------|---------------------|
 * | SocialTags         | SocialTags          |
 * | Genders            | Other               |
 * | InterestedIn       | Other               |
 * | SexualInterests    | Other               |
 * | CommunityKinks     | Other               |
 * | BodyPartsLiked     | BodyFeatures        |
 * | Kinks              | Other               |
 * | LookingFor         | Interests           |
 * | BodyTypes          | BodyFeatures        |
 * | Languages          | Languages           |
 * | Relationships      | Other               |
 * | Ethnicities        | Ethnicities         |
 * | BodyHair           | BodyFeatures        |
 * | FacialHair         | BodyFeatures        |
 */
export interface ISearchCardV6 {
  /** Present only in search options for staff who may send mass messages (website button). */
  MassMessageUrl?: string | null;
  // Range filters (min/max values)
  Age?: number[] | null;
  BMI?: number[] | null;
  Weight?: number[] | null;
  Height?: number[] | null;

  // Location filters
  Latitude?: number | null;
  Longitude?: number | null;
  IsNearMe?: boolean | null;

  // Username filter
  UserName?: string | null;

  // Multi-select tag filters (ProfileTags.SocialTags)
  SocialTags?: string[] | null;
  // Multi-select tag filters (ProfileTags.Other)
  Genders?: string[] | null;
  InterestedIn?: string[] | null;
  SexualInterests?: string[] | null;
  CommunityKinks?: string[] | null;
  Kinks?: string[] | null;
  Relationships?: string[] | null;
  // Multi-select tag filters (ProfileTags.BodyFeatures)
  BodyPartsLiked?: string[] | null;
  BodyTypes?: string[] | null;
  BodyHair?: string[] | null;
  FacialHair?: string[] | null;
  // Multi-select tag filters (ProfileTags.Languages)
  Languages?: string[] | null;
  // Multi-select tag filters (ProfileTags.Interests)
  LookingFor?: string[] | null;
  // Multi-select tag filters (ProfileTags.Ethnicities)
  Ethnicities?: string[] | null;

  // Boolean filters (null means the option is not available for the current community)
  HasPhotos?: boolean | null;
  HasPrivatePhotos?: boolean | null;
  IsOnline?: boolean | null;
  IsActive?: boolean | null;
  IsFollower?: boolean | null;
  ImFollowing?: boolean | null;
  IsNew?: boolean | null;
  ImTheirBodyType?: boolean | null;
}

/**
 * Map from SearchCardV6 property name to the ProfileTags translation section
 */
export const SEARCH_CARD_TRANSLATION_MAP: Record<string, string> = {
  SocialTags: 'SocialTags',
  Genders: 'Other',
  InterestedIn: 'Other',
  SexualInterests: 'Other',
  CommunityKinks: 'Other',
  BodyPartsLiked: 'BodyFeatures',
  Kinks: 'Other',
  LookingFor: 'Interests',
  BodyTypes: 'BodyFeatures',
  Languages: 'Languages',
  Relationships: 'Other',
  Ethnicities: 'Ethnicities',
  BodyHair: 'BodyFeatures',
  FacialHair: 'BodyFeatures',
};

/**
 * Creates an empty SearchCardV6 object with default values
 */
export function createEmptySearchCardV6(): ISearchCardV6 {
  return {
    Age: null,
    BMI: null,
    Weight: null,
    Height: null,
    Latitude: null,
    Longitude: null,
    IsNearMe: null,
    UserName: null,
    SocialTags: null,
    Genders: null,
    InterestedIn: null,
    SexualInterests: null,
    CommunityKinks: null,
    BodyPartsLiked: null,
    Kinks: null,
    LookingFor: null,
    BodyTypes: null,
    Languages: null,
    Relationships: null,
    Ethnicities: null,
    BodyHair: null,
    FacialHair: null,
    HasPhotos: false,
    HasPrivatePhotos: false,
    IsOnline: false,
    IsActive: false,
    IsFollower: false,
    ImFollowing: false,
    IsNew: false,
    ImTheirBodyType: false,
  };
}

/**
 * Checks if a SearchCardV6 object has no meaningful filters set (is effectively empty/default)
 */
export function isSearchCardEmpty(searchCard: ISearchCardV6): boolean {
  return Object.entries(searchCard).every(([, value]) => {
    if (value === null || value === undefined) return true;
    if (value === false) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  });
}

/**
 * Converts a SearchCardV6 to a URL query string for web navigation
 */
export function searchCardToQueryString(searchCard: ISearchCardV6): string {
  const params = new URLSearchParams();

  Object.entries(searchCard).forEach(([key, value]) => {
    if (value === null || value === undefined || value === false) return;
    if (value === true) {
      params.set(key, '1');
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(','));
      }
    } else if (typeof value === 'number') {
      params.set(key, value.toString());
    } else if (typeof value === 'string' && value.length > 0) {
      params.set(key, value);
    }
  });

  return params.toString();
}

// Range fields that should be parsed as number arrays
const RANGE_FIELDS = ['Age', 'BMI', 'Weight', 'Height'];

// Boolean fields
const BOOLEAN_FIELDS = [
  'IsNearMe',
  'HasPhotos',
  'HasPrivatePhotos',
  'IsOnline',
  'IsActive',
  'IsFollower',
  'ImFollowing',
  'IsNew',
  'ImTheirBodyType',
];

// Number fields (single numbers, not arrays)
const NUMBER_FIELDS = ['Latitude', 'Longitude'];

// String fields
const STRING_FIELDS = ['UserName'];

/**
 * Parses URL search params into a SearchCardV6 object
 */
export function parseSearchCardFromParams(
  params: Record<string, string | string[]>
): ISearchCardV6 | null {
  const searchCard = createEmptySearchCardV6();
  let hasAnyValue = false;

  Object.entries(params).forEach(([key, value]) => {
    if (!(key in searchCard)) return;

    const strValue = Array.isArray(value) ? value[0] : value;
    if (!strValue) return;

    if (BOOLEAN_FIELDS.includes(key)) {
      (searchCard as any)[key] = strValue === '1' || strValue === 'true';
      hasAnyValue = true;
    } else if (NUMBER_FIELDS.includes(key)) {
      const num = parseFloat(strValue);
      if (!isNaN(num)) {
        (searchCard as any)[key] = num;
        hasAnyValue = true;
      }
    } else if (RANGE_FIELDS.includes(key)) {
      const parts = strValue.split(',').map((s) => parseInt(s, 10));
      if (parts.length >= 2 && parts.every((n) => !isNaN(n))) {
        (searchCard as any)[key] = parts;
        hasAnyValue = true;
      }
    } else if (STRING_FIELDS.includes(key)) {
      (searchCard as any)[key] = strValue;
      hasAnyValue = true;
    } else {
      // Multi-select string arrays
      const parts = strValue.split(',').filter((s) => s.length > 0);
      if (parts.length > 0) {
        (searchCard as any)[key] = parts;
        hasAnyValue = true;
      }
    }
  });

  return hasAnyValue ? searchCard : null;
}
